<script setup>
import { ref, computed, onMounted } from 'vue';
import VueTabs from '~/components/VueTabs.vue';
import SubTabBookFlight from '~/components/EnrichPortal/BookingWidget/FlightSearch/SubTabBookFlight.vue';
import SubTabHistory from '~/components/EnrichPortal/BookingWidget/FlightSearch/SubTabHistory.vue';
import { useBookingWidgetUrlQuery } from '~/logic/composables/booking-widget/useBookingWidgetUrlQuery.js';
import MobileSubpage from '~/components/BookingWidget/MobileSubpage.vue';
import { isMobileViewport } from '~/logic/composables/breakpoints.js';
import { useAuthStore } from '~/logic/auth/auth-store.js';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';

const props = defineProps({
	// Main backend model properties
	general: { type: Object, default: () => ({
		isHideInDesktop: { type: Boolean, default: false },
		isHideInMobile: { type: Boolean, default: false },
		componentId: { type: String, default: '' },
		bookFlightTabLabel: { type: String, default: '' },
		bookFlightOverlayTitle: { type: String, default: '' },
		searchFlightCtaText: { type: String, default: '' },
		ondSelection: { type: Object, default: () => ({}) },
		datePicker: { type: Object, default: () => ({}) },
		passengerSelection: { type: Object, default: () => ({}) },
		topNoteOneWay: { type: String, default: '' },
		topNoteRoundTrip: { type: String, default: '' },
	}) },
	historyTab: { type: Object, default: () => ({
		historyTabLabel: { type: String, default: '' },
		tripDetailsCtaLabel: { type: String, default: '' },
		pnrLabel: { type: String, default: '' },
		eticketNumberLabel: { type: String, default: '' },
		tripTypeLabel: { type: String, default: '' },
		bookingDateLabel: { type: String, default: '' },
		statusLabel: { type: String, default: '' },
		creditRedeemedLabel: { type: String, default: '' },
		noResults: { type: Object, default: () => ({
			imageUrl: { type: String, default: '' },
			title: { type: String, default: '' },
			message: { type: String, default: '' },
		}) },
	}) },
	flightSearchApiUrl: { type: String, default: '' },
	componentIdAA: { type: String, default: '' },
	cabinClassCashList: { type: Array, default: () => [] },
	cabinClassMilesList: { type: Array, default: () => [] },
	togglePointsApiURL: { type: String, default: '' },
	enrichAlwaysOnCountriesData: { type: Array, default: () => [] },
	countryVariants: { type: Array, default: () => [] },
	labels: { type: Object, default: () => ({}) },
	mobileAddOns: { type: Object, default: () => ({}) },
});

const emit = defineEmits([
	'update:active-sub-tab',
]);

// Composables
const authStore = useAuthStore();
const { isEditorMode } = useGlobalAEMState();

// Reactive data
const activeSubTab = ref('book-flight');
const isMobileSubpageVisible = ref(false);
const vueTabsComponent = ref(null);
const subTabBookFlightEl = ref(null);
const subTabHistoryEl = ref(null);
const selectedZone = ref(null);
const expireDate = ref(null);
const isFromExpiredFlypass = ref(false);
const remainingQuota = ref(null);

// Computed properties
const isLoggedIn = computed(() => authStore.isLoggedIn);

// Methods
const { registerQueryChange } = useBookingWidgetUrlQuery({
	mainTabName: 'enrich-flypass-flight-search',
});

registerQueryChange(async (query) => {
	if (!query) return;

	const newSubTab = query.subtab ?? '';
	if (newSubTab) {
		activeSubTab.value = newSubTab;
		emit('update:active-sub-tab', newSubTab);
	}
});

const handleTabChange = (tabId) => {
	activeSubTab.value = tabId;
};

const handleSaveSearchData = (data) => {
	// Handle search data saving logic
	console.log('Save search data:', data);
};

const redeemFlypassData = sessionStorage.getItem('redeemFlypassData');
if (redeemFlypassData) {
	try {
		const parsedData = JSON.parse(redeemFlypassData);
		selectedZone.value = parsedData.zone;
		expireDate.value = parsedData.expireDate;
		if (parsedData.remainingQuota !== undefined) {
			remainingQuota.value = parsedData.remainingQuota;
		}
	} catch (error) {
		console.error('Error parsing redeemFlypassData:', error);
	}
}

const selectedFlypassData = sessionStorage.getItem('selectedFlypassData');
if (selectedFlypassData) {
	try {
		const parsedData = JSON.parse(selectedFlypassData);
		isFromExpiredFlypass.value = parsedData.isExpired || false;
		remainingQuota.value = parsedData.remainingQuota;
	} catch (error) {
		console.error('Error parsing selectedFlypassData:', error);
	}
}

const shouldHideBookFlightTab = computed(() => {
	return isFromExpiredFlypass.value || (remainingQuota.value !== null && Number(remainingQuota.value) === 0);
});

// Lifecycle
onMounted(() => {
	// console.log(props);
});
</script>

<template>
<div class="EnrichFlypassFlightSearch">
	<h5 v-if="isEditorMode" class="title-editor-mode">
		<icon-fas-plane class="mr-2 fill-current inline-block" />
		<span>Booking Widget: Enrich Flypass Flight Search</span>
	</h5>
	<div class="generic-container">
		<!-- Overlay Title -->
		<div v-if="props.general?.bookFlightOverlayTitle" class="mb-4">
			<h2 class="text-2xl font-bold">{{ props.general.bookFlightOverlayTitle }}</h2>
		</div>

		<VueTabs
			ref="vueTabsComponent"
			v-model="activeSubTab"
			:syncURLQuery="true"
			:dormantMode="isEditorMode"
			renderPanelsWithVShow
			@update:modelValue="handleTabChange"
		>
			<!-- Book Flight Tab -->
			<template v-if="!shouldHideBookFlightTab" #tab-book-flight>
				<div class="flex items-center justify-center p-4">
					<span class="inline-block ml-1">{{ props.general?.bookFlightTabLabel }}</span>
				</div>
			</template>

			<!-- History Tab -->
			<template v-if="isLoggedIn" #tab-history>
				<div class="flex items-center justify-center p-4">
					<span class="inline-block ml-1">{{ props.historyTab?.historyTabLabel }}</span>
				</div>
			</template>

			<!-- Book Flight Panel -->
			<div v-if="!shouldHideBookFlightTab" identifier="book-flight">
				<SubTabBookFlight
					ref="subTabBookFlightEl"
					class="pt-6"
					:ondSelection="props.general?.ondSelection"
					:topNoteOneWay="props.general?.topNoteOneWay"
					:topNoteRoundTrip="props.general?.topNoteRoundTrip"
					:datePicker="props.general?.datePicker"
					:passengerSelection="props.general?.passengerSelection"
					:searchFlightCTAText="props.general?.searchFlightCtaText"
					:hidePromoCodeField="true"
					:selectedZone="selectedZone"
					:flightSearchApiUrl="props.flightSearchApiUrl"
					:expireDate="expireDate"
					:remainingQuota="remainingQuota"
					@save-search-data="handleSaveSearchData"
				></SubTabBookFlight>
			</div>

			<!-- History Panel -->
			<div v-if="isLoggedIn" identifier="history">
				<SubTabHistory
					ref="subTabHistoryEl"
					class="mt-5"
					:tripDetailsCtaLabel="props.historyTab?.tripDetailsCtaLabel"
					:pnrLabel="props.historyTab?.pnrLabel"
					:eTicketNumberLabel="props.historyTab?.eticketNumberLabel"
					:tripTypeLabel="props.historyTab?.tripTypeLabel"
					:bookingDateLabel="props.historyTab?.bookingDateLabel"
					:statusLabel="props.historyTab?.statusLabel"
					:creditRedeemedLabel="props.historyTab?.creditRedeemedLabel"
					:noResults="props.historyTab?.noResults"
				></SubTabHistory>
			</div>
		</VueTabs>
	</div>
</div>
</template>

<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.EnrichFlypassFlightSearch {

}

</style>
